import { CheckBox } from '../../../Form/CheckBox';
import { InputText } from '../../../Form/InputText';
import { Select } from '../../../Form/Select';
import { InnerTabSpace } from '../../../Layout/InnerLayout/InnerTabSpace';
import { TableBody } from '../../../Table/TableBody';
import { TableTrFormLabel } from '../../../Table/TableTr/TableTrFromLabel';

import type { InvalidErrors } from './createInvalidErrors';
import type { CheckboxAttribute } from '../../../../types/CheckboxAttribute';
import type { SelectAttribute } from '../../../../types/SelectAttribute';
import type { FC, ChangeEventHandler } from 'react';

type Props = {
  qualificationAttributes: CheckboxAttribute[];
  employmentAttributes: SelectAttribute[];
  affiliationAttributes: CheckboxAttribute[];
  activityAreaAttributes: CheckboxAttribute[];
  licenseIdAttributes: CheckboxAttribute[];
  onChangeQualificationCodeList: ChangeEventHandler<HTMLInputElement>;
  onChangeEmploymentId: ChangeEventHandler<HTMLSelectElement>;
  onChangeAffiliationIds: ChangeEventHandler<HTMLInputElement>;
  onChangeActivityAreaIds: ChangeEventHandler<HTMLInputElement>;
  onChangeLicenseIds: ChangeEventHandler<HTMLInputElement>;
  onChangeCustomLicense: ChangeEventHandler<HTMLInputElement>;
  onChangeNurseLicenseNumber: ChangeEventHandler<HTMLInputElement>;
  qualificationCodeList: number[];
  selectedEmploymentId: number;
  affiliationIds: number[];
  activityAreaIds: number[];
  licenseIds: number[];
  customLicense: string;
  nurseLicenseNumber: string;
  invalidErrors?: InvalidErrors;
};

const countZero = 0;

// eslint-disable-next-line max-lines-per-function
export const QualificationsAndWorkTabPanel: FC<Props> = ({
  qualificationAttributes,
  employmentAttributes,
  affiliationAttributes,
  activityAreaAttributes,
  licenseIdAttributes,
  onChangeQualificationCodeList,
  onChangeEmploymentId,
  onChangeAffiliationIds,
  onChangeActivityAreaIds,
  onChangeLicenseIds,
  onChangeCustomLicense,
  onChangeNurseLicenseNumber,
  qualificationCodeList,
  selectedEmploymentId,
  affiliationIds,
  activityAreaIds,
  licenseIds,
  customLicense,
  nurseLicenseNumber,
  invalidErrors,
}) => {
  const qualification =
    // eslint-disable-next-line no-negated-condition
    qualificationCodeList.length !== countZero
      ? qualificationAttributes.map((checkboxAttribute) => {
          if (qualificationCodeList.includes(checkboxAttribute.value)) {
            return {
              id: checkboxAttribute.id,
              name: checkboxAttribute.name,
              value: checkboxAttribute.value,
              label: checkboxAttribute.label,
              checked: true,
            };
          }

          return checkboxAttribute;
        })
      : qualificationAttributes;

  const affiliationIdList =
    // eslint-disable-next-line no-negated-condition
    affiliationIds.length !== countZero
      ? affiliationAttributes.map((checkboxAttribute) => {
          if (affiliationIds.includes(checkboxAttribute.value)) {
            return {
              id: checkboxAttribute.id,
              name: checkboxAttribute.name,
              value: checkboxAttribute.value,
              label: checkboxAttribute.label,
              checked: true,
            };
          }

          return checkboxAttribute;
        })
      : affiliationAttributes;

  const activityAreaIdList =
    // eslint-disable-next-line no-negated-condition
    activityAreaIds.length !== countZero
      ? activityAreaAttributes.map((checkboxAttribute) => {
          if (activityAreaIds.includes(checkboxAttribute.value)) {
            return {
              id: checkboxAttribute.id,
              name: checkboxAttribute.name,
              value: checkboxAttribute.value,
              label: checkboxAttribute.label,
              checked: true,
            };
          }

          return checkboxAttribute;
        })
      : activityAreaAttributes;

  const licenseIdList =
    // eslint-disable-next-line no-negated-condition
    licenseIds.length !== countZero
      ? licenseIdAttributes.map((checkboxAttribute) => {
          if (licenseIds.includes(checkboxAttribute.value)) {
            return {
              id: checkboxAttribute.id,
              name: checkboxAttribute.name,
              value: checkboxAttribute.value,
              label: checkboxAttribute.label,
              checked: true,
            };
          }

          return checkboxAttribute;
        })
      : licenseIdAttributes;

  return (
    <InnerTabSpace>
      <TableBody>
        <TableTrFormLabel
          id="qualification"
          required={false}
          errorMessage={
            invalidErrors?.qualificationCodeListError?.reason
              ? invalidErrors?.qualificationCodeListError?.reason
              : ''
          }
          title="保有資格"
        >
          <CheckBox
            columns="three"
            attributes={qualification}
            onChange={onChangeQualificationCodeList}
            isUseChecked={true}
          />
        </TableTrFormLabel>
        <TableTrFormLabel
          id="employmentId"
          required={false}
          errorMessage={
            invalidErrors?.employmentIdError?.reason
              ? invalidErrors?.employmentIdError?.reason
              : ''
          }
          title="勤務先種別"
        >
          <Select
            id="employmentId"
            width="default"
            attributes={employmentAttributes}
            onChange={onChangeEmploymentId}
            defaultValue={String(selectedEmploymentId)}
          />
        </TableTrFormLabel>
        <TableTrFormLabel
          id="affiliationId"
          required={false}
          errorMessage={
            invalidErrors?.affiliationIdsError?.reason
              ? invalidErrors?.affiliationIdsError?.reason
              : ''
          }
          title="所属科"
        >
          <CheckBox
            columns="three"
            attributes={affiliationIdList}
            onChange={onChangeAffiliationIds}
            isUseChecked={true}
          />
        </TableTrFormLabel>
        <TableTrFormLabel
          id="activityAreaId"
          required={false}
          errorMessage={
            invalidErrors?.activityAreaIdsError?.reason
              ? invalidErrors?.activityAreaIdsError?.reason
              : ''
          }
          title="活動領域"
        >
          <CheckBox
            columns="three"
            attributes={activityAreaIdList}
            onChange={onChangeActivityAreaIds}
            isUseChecked={true}
          />
        </TableTrFormLabel>
        <TableTrFormLabel
          id="licenseId"
          required={false}
          errorMessage={
            invalidErrors?.licenseIdsError?.reason
              ? invalidErrors?.licenseIdsError?.reason
              : ''
          }
          title="認定資格"
        >
          <CheckBox
            columns="three"
            attributes={licenseIdList}
            onChange={onChangeLicenseIds}
            isUseChecked={true}
          />
        </TableTrFormLabel>
        <TableTrFormLabel
          id="customLicense"
          required={false}
          errorMessage={
            invalidErrors?.customLicenseError?.reason
              ? invalidErrors?.customLicenseError?.reason
              : ''
          }
          title="認定資格（その他）[自由記述]"
        >
          <InputText
            name="customLicense"
            id="customLicense"
            type="text"
            width="default"
            placeholder="その他記載自由"
            defaultValue={customLicense}
            onChange={onChangeCustomLicense}
          />
        </TableTrFormLabel>
        <TableTrFormLabel
          id="nurseLicenseNumber"
          required={false}
          errorMessage={
            invalidErrors?.nurseLicenseNumberError?.reason
              ? invalidErrors?.nurseLicenseNumberError?.reason
              : ''
          }
          title="看護師免許番号"
        >
          <InputText
            name="nurseLicenseNumber"
            id="nurseLicenseNumber"
            type="text"
            width="default"
            placeholder=""
            defaultValue={nurseLicenseNumber}
            onChange={onChangeNurseLicenseNumber}
          />
        </TableTrFormLabel>
      </TableBody>
    </InnerTabSpace>
  );
};
